import { forwardRef } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';

import type { WrappedReportData } from './growth.types';

type Props = { data: WrappedReportData };

const SHARE_WIDTH = 1080;
const SHARE_HEIGHT = 1080;

const HOUR_PERSONA_COPY: Record<string, string> = {
  early_bird: 'Early Bird',
  morning: 'Morning Person',
  afternoon: 'Afternoon Thinker',
  night_owl: 'Night Owl',
  late_night: 'Late-Night Hacker',
  explorer: 'Explorer',
};

const formatHour = (hour: number | null): string => {
  if (hour === null) return '—';
  const h = ((hour + 11) % 12) + 1;
  const suffix = hour < 12 ? 'AM' : 'PM';
  return `${h} ${suffix}`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

export const GrowthShareCard = forwardRef<HTMLDivElement, Props>(
  ({ data }, ref) => {
    const topTools = data.tools.slice(0, 5);
    const peakDate = formatDate(data.peakDayDate);
    const rhythm = HOUR_PERSONA_COPY[data.rhythm.hourPersona] ?? 'Explorer';

    return (
      <div
        ref={ref}
        style={{
          width: SHARE_WIDTH,
          height: SHARE_HEIGHT,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
          background:
            'radial-gradient(circle at 20% 0%, #1e293b 0%, #0b1120 55%, #050814 100%)',
          color: '#f8fafc',
          padding: 56,
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          boxSizing: 'border-box',
        }}
      >
        <YStack style={{ gap: 16, alignItems: 'center', textAlign: 'center' }}>
          <XStack style={{ gap: 12, alignItems: 'center' }}>
            <AppLogo size={28} />
            <span
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#f8fafc',
                letterSpacing: 0.2,
              }}
            >
              Littlebird
            </span>
            <span
              style={{
                width: 1,
                height: 18,
                background: 'rgba(148, 163, 184, 0.4)',
                marginLeft: 4,
                marginRight: 4,
              }}
            />
            <span
              style={{
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: 4,
                textTransform: 'uppercase',
                color: '#94a3b8',
              }}
            >
              {data.periodLabel} &middot; AI Report
            </span>
          </XStack>

          <h1
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: 60,
              lineHeight: 1.05,
              margin: 0,
              color: '#f8fafc',
            }}
          >
            &ldquo;{data.headline}&rdquo;
          </h1>

          <XStack style={{ gap: 10, alignItems: 'center' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 16px',
                borderRadius: 9999,
                background: 'rgba(148, 163, 184, 0.15)',
                color: '#e2e8f0',
                fontSize: 18,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}
            >
              <Icons.user style={{ width: 16, height: 16, color: '#94a3b8' }} />
              {data.persona}
            </span>
          </XStack>
          {data.topPercentile !== null && (
            <span
              style={{
                fontSize: 18,
                color: '#94a3b8',
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}
            >
              🏆 Top{' '}
              <span
                style={{
                  fontWeight: 600,
                  color: '#f8fafc',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {data.topPercentile}%
              </span>{' '}
              on Littlebird
            </span>
          )}
        </YStack>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            gap: 16,
          }}
        >
          <StatTile
            label="Top Tool"
            value={data.topTool?.name ?? '—'}
            badge={data.topTool ? `${data.topTool.sharePct}% of AI` : 'No data'}
          />
          <StatTile
            label="Days Active"
            value={`${data.daysActive}`}
            badge={`${data.daysActive}/${data.totalDays}`}
          />
          <StatTile
            label="Longest Streak"
            value={`${data.longestStreak}d`}
            badge="🔥"
          />
          <StatTile label="Peak Day" value={peakDate} badge="" />
        </div>

        <YStack style={{ gap: 18 }}>
          <SectionLabel>Top AI Tools</SectionLabel>
          <YStack style={{ gap: 12 }}>
            {topTools.length === 0 ? (
              <span style={{ fontSize: 18, color: '#94a3b8' }}>
                No AI tools detected.
              </span>
            ) : (
              topTools.map((t, i) => (
                <XStack key={t.name} style={{ gap: 16, alignItems: 'center' }}>
                  <span
                    style={{
                      fontSize: 18,
                      color: '#64748b',
                      width: 28,
                    }}
                  >
                    {i + 1}.
                  </span>
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 600,
                      color: '#f8fafc',
                      width: 160,
                    }}
                  >
                    {t.name}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 10,
                      borderRadius: 9999,
                      background: 'rgba(148, 163, 184, 0.18)',
                    }}
                  >
                    <div
                      style={{
                        height: 10,
                        width: `${Math.min(t.percentage, 100)}%`,
                        borderRadius: 9999,
                        background:
                          'linear-gradient(90deg, #60a5fa 0%, #a78bfa 100%)',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 14,
                      color: '#cbd5e1',
                      width: 110,
                      textAlign: 'right',
                    }}
                  >
                    {t.captureCount} frames
                  </span>
                </XStack>
              ))
            )}
          </YStack>
        </YStack>

        <XStack style={{ gap: 16 }}>
          <StatTile
            label="Littlebird"
            value={`${data.littlebirdUsage.chatCount}`}
            badge={`${data.littlebirdUsage.messageCount} msgs`}
          />
          <StatTile
            label="Rhythm"
            value={rhythm}
            badge={formatHour(data.rhythm.peakHourLocal)}
          />
          <StatTile
            label="New Tools"
            value={`${data.growth.toolsNewThisMonth.length}`}
            badge={
              data.growth.showMultiplier
                ? `${data.growth.aiCapturesGrowth}× MoM`
                : ''
            }
          />
        </XStack>

        <div style={{ flex: 1 }} />

        <XStack
          style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingTop: 20,
            borderTop: '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
          <span style={{ fontSize: 18, color: '#94a3b8' }}>littlebird.ai</span>
        </XStack>
      </div>
    );
  }
);
GrowthShareCard.displayName = 'GrowthShareCard';

type TileProps = { label: string; value: string; badge: string };

const StatTile = ({ label, value, badge }: TileProps) => (
  <YStack
    style={{
      flex: 1,
      padding: 20,
      borderRadius: 18,
      background: 'rgba(15, 23, 42, 0.6)',
      border: '1px solid rgba(148, 163, 184, 0.15)',
      gap: 6,
      minWidth: 0,
    }}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 3,
        textTransform: 'uppercase',
        color: '#94a3b8',
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 32,
        fontWeight: 700,
        color: '#f8fafc',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.05,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
    {badge && <span style={{ fontSize: 12, color: '#cbd5e1' }}>{badge}</span>}
  </YStack>
);

const SectionLabel = ({ children }: { children: string }) => (
  <span
    style={{
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 3,
      textTransform: 'uppercase',
      color: '#94a3b8',
    }}
  >
    {children}
  </span>
);
